export const extractNameFromEmail = (email: string) => {
  if (!email) return;
  return email.split("@")[0];
};

/**
 * Join a base URL and a path segment with EXACTLY one slash between them,
 * regardless of whether either side already has one.
 *
 * Why this exists: VITE_API_BASE_LESSON_URL ends in "/" (".../api/"), and
 * a lesson's `.url` field (as stored by backend/extraction.service.ts:
 * `/${lessonSlug}`) ALSO starts with "/" — naively templating them
 * together produces ".../api//what-is-money/". `.slug` (bare, no leading
 * slash) doesn't have this problem, which is why it looked "correct"
 * wherever it was used — but relying on callers to remember which field is
 * safe is exactly how this bug happened. Use this wherever a lesson link
 * is built, regardless of which field you're joining in.
 */
export const joinLessonUrl = (base: string, segment: string): string =>
  `${base.replace(/\/+$/, "")}/${segment.replace(/^\/+/, "")}/`;

/**
 * MUST match backend Helpers.createSlug (helper.ts) exactly — this is used
 * to predict, client-side, what slug a lesson name will extract to, so
 * LessonZipDropZone can check for a pre-existing lesson with that slug
 * BEFORE starting the upload.
 */
export const createSlug = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");
// BTC to Sats converter
export const btcToSats = (btc: number): number => Math.round(btc * 100000000);

// Main formatter - accepts BTC (string or number) and converts to sats first
export const formatIntoSats = (
  btcInput: string | number,
  decimals: number = 1,
): string => {
  // First convert to number
  const btc = typeof btcInput === "string" ? parseFloat(btcInput) : btcInput;

  if (isNaN(btc)) {
    return "Invalid BTC value";
  }

  // Convert BTC to sats
  const sats = btcToSats(btc);

  // Now format the sats
  if (sats < 10000) {
    return `${sats} sats`;
  }

  if (sats < 1000000) {
    return `${(sats / 1000).toFixed(decimals)}k sats`;
  }

  if (sats < 1000000000) {
    return `${(sats / 1000000).toFixed(decimals)}m sats`;
  }

  return `${(sats / 1000000000).toFixed(decimals)}b sats`;
};
